import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { saveSwipePreferences, getSwipePreferences } from '../services/api';
import api from '../services/api';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import { 
  Heart, MapPin, Euro, ArrowLeft, Save, Check, Edit2,
  GraduationCap, Briefcase, Globe, Users, Baby, 
  UserCircle, Zap, User, Sparkles, Crown, Bell, BellOff, X,
  Camera, Palette, RefreshCw, Loader2, Home, ChevronRight,
  Building2, Wand2, Calendar, LogOut, Search, Star, Shield,
  TrendingUp, CheckCircle, Settings, Award, Target, MessageSquare
} from 'lucide-react';


// Fun avatar options - simple solid colors
const AVATAR_OPTIONS = [
  { id: 'cool-guy', emoji: '😎', bg: 'bg-[#2E7D32]', label: { nl: 'Cool', en: 'Cool' } },
  { id: 'happy', emoji: '😊', bg: 'bg-[#2E7D32]', label: { nl: 'Blij', en: 'Happy' } },
  { id: 'nerd', emoji: '🤓', bg: 'bg-[#2E7D32]', label: { nl: 'Slim', en: 'Smart' } },
  { id: 'chill', emoji: '😌', bg: 'bg-[#2E7D32]', label: { nl: 'Chill', en: 'Chill' } },
  { id: 'excited', emoji: '🤩', bg: 'bg-[#2E7D32]', label: { nl: 'Enthousiast', en: 'Excited' } },
  { id: 'professional', emoji: '🧑‍💼', bg: 'bg-[#1F3A5F]', label: { nl: 'Zakelijk', en: 'Professional' } },
  { id: 'creative', emoji: '🎨', bg: 'bg-[#2E7D32]', label: { nl: 'Creatief', en: 'Creative' } },
  { id: 'adventurer', emoji: '🌟', bg: 'bg-[#1F3A5F]', label: { nl: 'Avonturier', en: 'Adventurer' } },
  { id: 'friendly', emoji: '👋', bg: 'bg-[#2E7D32]', label: { nl: 'Vriendelijk', en: 'Friendly' } },
  { id: 'studious', emoji: '📚', bg: 'bg-[#1F3A5F]', label: { nl: 'Studieus', en: 'Studious' } },
  { id: 'party', emoji: '🎉', bg: 'bg-[#2E7D32]', label: { nl: 'Feestelijk', en: 'Festive' } },
  { id: 'zen', emoji: '🧘', bg: 'bg-[#1F3A5F]', label: { nl: 'Zen', en: 'Zen' } },
];

// Rental duration options - matching backend IDs
const RENTAL_DURATION_OPTIONS = [
  { id: 'short_1_3', label: { nl: '1-3 maanden', en: '1-3 months' } },
  { id: 'medium_3_6', label: { nl: '3-6 maanden', en: '3-6 months' } },
  { id: 'medium_6_12', label: { nl: '6-12 maanden', en: '6-12 months' } },
  { id: 'long_1_year', label: { nl: '1 jaar of langer', en: '1 year or longer' } },
  { id: 'indefinite', label: { nl: 'Onbepaalde tijd', en: 'Indefinite' } },
];

// All Dutch cities
const ALL_CITIES = [
  { id: 'Amsterdam', label: 'Amsterdam', popular: true },
  { id: 'Rotterdam', label: 'Rotterdam', popular: true },
  { id: 'Utrecht', label: 'Utrecht', popular: true },
  { id: 'Den Haag', label: 'Den Haag', popular: true },
  { id: 'Eindhoven', label: 'Eindhoven', popular: true },
  { id: 'Groningen', label: 'Groningen', popular: true },
  { id: 'Tilburg', label: 'Tilburg', popular: true },
  { id: 'Almere', label: 'Almere' },
  { id: 'Breda', label: 'Breda', popular: true },
  { id: 'Nijmegen', label: 'Nijmegen', popular: true },
  { id: 'Arnhem', label: 'Arnhem', popular: true },
  { id: 'Haarlem', label: 'Haarlem' },
  { id: 'Enschede', label: 'Enschede' },
  { id: 'Maastricht', label: 'Maastricht' },
  { id: 'Leiden', label: 'Leiden' },
  { id: 'Delft', label: 'Delft' },
  { id: 'Amersfoort', label: 'Amersfoort' },
  { id: 'Zwolle', label: 'Zwolle' },
  { id: 'Apeldoorn', label: 'Apeldoorn' },
  { id: 'Dordrecht', label: 'Dordrecht' },
  { id: "'s-Hertogenbosch", label: "'s-Hertogenbosch" },
  { id: 'Hoofddorp', label: 'Hoofddorp' },
  { id: 'Zoetermeer', label: 'Zoetermeer' },
  { id: 'Deventer', label: 'Deventer' },
  { id: 'Alkmaar', label: 'Alkmaar' },
  { id: 'Heerlen', label: 'Heerlen' },
  { id: 'Venlo', label: 'Venlo' },
  { id: 'Leeuwarden', label: 'Leeuwarden' },
  { id: 'Hilversum', label: 'Hilversum' },
  { id: 'Amstelveen', label: 'Amstelveen' },
  { id: 'Oss', label: 'Oss' },
  { id: 'Schiedam', label: 'Schiedam' },
  { id: 'Helmond', label: 'Helmond' },
  { id: 'Vlaardingen', label: 'Vlaardingen' },
  { id: 'Almelo', label: 'Almelo' },
  { id: 'Gouda', label: 'Gouda' },
  { id: 'Zaandam', label: 'Zaandam' },
  { id: 'Lelystad', label: 'Lelystad' },
  { id: 'Purmerend', label: 'Purmerend' },
  { id: 'Roosendaal', label: 'Roosendaal' },
  { id: 'Hoorn', label: 'Hoorn' },
  { id: 'Ede', label: 'Ede' },
  { id: 'Alphen aan den Rijn', label: 'Alphen aan den Rijn' },
  { id: 'Bergen op Zoom', label: 'Bergen op Zoom' },
  { id: 'Nieuwegein', label: 'Nieuwegein' },
  { id: 'Veenendaal', label: 'Veenendaal' },
  { id: 'Zeist', label: 'Zeist' },
  { id: 'Middelburg', label: 'Middelburg' },
  { id: 'Doetinchem', label: 'Doetinchem' },
  { id: 'Roermond', label: 'Roermond' },
];

// Radius options
const RADIUS_OPTIONS = [
  { value: 0, label: { nl: 'Alleen geselecteerde steden', en: 'Selected cities only' } },
  { value: 5, label: { nl: '+5 km', en: '+5 km' } },
  { value: 10, label: { nl: '+10 km', en: '+10 km' } },
  { value: 15, label: { nl: '+15 km', en: '+15 km' } },
  { value: 25, label: { nl: '+25 km', en: '+25 km' } },
  { value: 50, label: { nl: '+50 km', en: '+50 km' } },
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading, refreshUser, logout } = useAuth();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [bio, setBio] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [navigatingToPremium, setNavigatingToPremium] = useState(false);
  
  const [preferences, setPreferences] = useState({
    profileType: '',
    cities: [],
    types: [],
    minPrice: '',
    maxPrice: '',
    rentalDuration: '',
    hasPets: null,
    needsRegistration: null,
    searchRadius: 0,
    autoMessage: ''
  });

  const isVerhuurder = user?.userType === 'verhuurder';
  const hasSubscription = user?.subscription === 'swipematch' || user?.trialEndDate;

  // Handle navigation to premium with loading animation
  const handleNavigateToPremium = async () => {
    setNavigatingToPremium(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    navigate('/premium#plans');
  };

  // Filter cities based on search
  const filteredCities = ALL_CITIES.filter(city => 
    city.label.toLowerCase().includes(citySearch.toLowerCase())
  );
  const popularCities = filteredCities.filter(c => c.popular);
  const otherCities = filteredCities.filter(c => !c.popular);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadUserData();
  }, [isAuthenticated, authLoading]);

  const loadUserData = async () => {
    try {
      const res = await getSwipePreferences();
      if (res.preferences) {
        setPreferences({
          profileType: res.preferences.profileType || '',
          cities: res.preferences.cities || [],
          types: res.preferences.types || [],
          minPrice: res.preferences.minPrice || '',
          maxPrice: res.preferences.maxPrice || '',
          rentalDuration: res.preferences.rentalDuration || '',
          hasPets: res.preferences.hasPets ?? null,
          needsRegistration: res.preferences.needsRegistration ?? null,
          searchRadius: res.preferences.searchRadius || 0,
          autoMessage: res.preferences.autoMessage || ''
        });
      }
      setSelectedAvatar(user?.avatar || null);
      setProfileImage(user?.profileImage || null);
      setBio(user?.bio || '');
      setEmailNotifications(user?.emailNotifications !== false);
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAvatar = async (avatar) => {
    try {
      await api.patch('/user/settings', { avatar: avatar.id });
      setSelectedAvatar(avatar.id);
      setShowAvatarModal(false);
      await refreshUser();
      toast({ title: language === 'nl' ? 'Avatar bijgewerkt!' : 'Avatar updated!' });
    } catch (error) {
      toast({ title: language === 'nl' ? 'Er ging iets mis' : 'Something went wrong', variant: 'destructive' });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: language === 'nl' ? 'Afbeelding te groot (max 2MB)' : 'Image too large (max 2MB)', variant: 'destructive' });
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data?.url) {
        await api.patch('/user/settings', { profileImage: response.data.url });
        setProfileImage(response.data.url);
        await refreshUser();
        toast({ title: language === 'nl' ? 'Foto geüpload!' : 'Photo uploaded!' });
      }
    } catch (error) {
      toast({ title: language === 'nl' ? 'Upload mislukt' : 'Upload failed', variant: 'destructive' });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveBio = async () => {
    try {
      await api.patch('/user/settings', { bio });
      await refreshUser();
      toast({ title: language === 'nl' ? 'Bio opgeslagen!' : 'Bio saved!' });
    } catch (error) {
      toast({ title: language === 'nl' ? 'Er ging iets mis' : 'Something went wrong', variant: 'destructive' });
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await saveSwipePreferences({
        ...preferences,
        minPrice: preferences.minPrice ? parseFloat(preferences.minPrice) : null,
        maxPrice: preferences.maxPrice ? parseFloat(preferences.maxPrice) : null
      });
      setEditMode(false);
      toast({ title: language === 'nl' ? 'Voorkeuren opgeslagen!' : 'Preferences saved!' });
    } catch (error) {
      toast({ title: language === 'nl' ? 'Er ging iets mis' : 'Something went wrong', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await api.post('/subscription/cancel');
      await refreshUser();
      setShowCancelModal(false);
      toast({ title: language === 'nl' ? 'Abonnement opgezegd' : 'Subscription cancelled' });
    } catch (error) {
      toast({ title: language === 'nl' ? 'Er ging iets mis' : 'Something went wrong', variant: 'destructive' });
    } finally {
      setCancelling(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleNotifications = async () => {
    try {
      await api.patch('/user/settings', { emailNotifications: !emailNotifications });
      setEmailNotifications(!emailNotifications);
      toast({ title: emailNotifications 
        ? (language === 'nl' ? 'Meldingen uitgeschakeld' : 'Notifications disabled')
        : (language === 'nl' ? 'Meldingen ingeschakeld' : 'Notifications enabled')
      });
    } catch (error) {
      toast({ title: language === 'nl' ? 'Er ging iets mis' : 'Something went wrong', variant: 'destructive' });
    }
  };

  const currentAvatar = AVATAR_OPTIONS.find(a => a.id === selectedAvatar) || AVATAR_OPTIONS[0];
  
  const profileTypes = [
    { id: 'student', emoji: '🎓', label: 'Student', color: 'bg-[#2E7D32]' },
    { id: 'starter', emoji: '🚀', label: 'Starter', color: 'bg-[#2E7D32]' },
    { id: 'professional', emoji: '💼', label: 'Professional', color: 'bg-[#1F3A5F]' },
    { id: 'expat', emoji: '🌍', label: 'Expat', color: 'bg-[#1F3A5F]' },
    { id: 'koppel', emoji: '💑', label: 'Koppel', color: 'bg-[#2E7D32]' },
    { id: 'gezin', emoji: '👨‍👩‍👧', label: 'Gezin', color: 'bg-[#2E7D32]' },
  ];
  
  const currentProfileType = profileTypes.find(p => p.id === preferences.profileType);

  // Calculate profile completeness
  const getProfileCompleteness = () => {
    let score = 0;
    if (preferences.profileType) score += 20;
    if (preferences.cities?.length > 0) score += 20;
    if (preferences.types?.length > 0) score += 15;
    if (preferences.maxPrice) score += 15;
    if (bio?.length > 20) score += 15;
    if (profileImage || selectedAvatar) score += 15;
    return score;
  };

  const completeness = getProfileCompleteness();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1F3A5F] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#2E7D32] animate-spin mx-auto mb-4" />
          <p className="text-white/60">{language === 'nl' ? 'Profiel laden...' : 'Loading profile...'}</p>
        </div>
      </div>
    );
  }

  // Loading overlay for premium navigation
  if (navigatingToPremium) {
    return (
      <div className="fixed inset-0 bg-[#1F3A5F] flex items-center justify-center z-50">
        <div className="text-center">
          <div className="mb-6 animate-pulse">
            <SwipeMatchLogo size="large" variant="white" className="justify-center" />
          </div>
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce"></div>
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce" style={{animationDelay: '0.15s'}}></div>
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce" style={{animationDelay: '0.3s'}}></div>
            </div>
            <p className="text-white/80 text-sm font-medium">
              {language === 'nl' ? 'Abonnementen laden...' : 'Loading plans...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1F3A5F]" data-testid="profile-page">
      <Toaster />
      
      {/* Avatar Selection Modal */}
      {showAvatarModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-black text-[#2A2A2A]">{language === 'nl' ? 'Kies je avatar' : 'Choose your avatar'}</h3>
                <p className="text-sm text-[#9E9E9E]">{language === 'nl' ? 'Laat zien wie je bent!' : 'Show who you are!'}</p>
              </div>
              <button onClick={() => setShowAvatarModal(false)} className="w-10 h-10 bg-[#F7F7F7] rounded-full flex items-center justify-center hover:bg-[#E5E5E5] transition-colors">
                <X className="w-5 h-5 text-[#9E9E9E]" />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {AVATAR_OPTIONS.map((avatar) => (
                <button
                  key={avatar.id}
                  onClick={() => handleSaveAvatar(avatar)}
                  className={`aspect-square rounded-2xl ${avatar.bg} flex items-center justify-center text-3xl hover:scale-110 transition-transform shadow-lg ${
                    currentAvatar?.id === avatar.id ? 'ring-4 ring-[#2E7D32] ring-offset-2' : ''
                  }`}
                >
                  {avatar.emoji}
                </button>
              ))}
            </div>
            <p className="text-xs text-[#9E9E9E] text-center mt-6">
              {language === 'nl' ? 'Je avatar is zichtbaar voor verhuurders' : 'Your avatar is visible to landlords'}
            </p>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-black text-[#2A2A2A]">{language === 'nl' ? 'Abonnement opzeggen?' : 'Cancel subscription?'}</h3>
              <p className="text-[#9E9E9E] mt-2">
                {language === 'nl' ? 'Je hebt nog toegang tot het einde van de periode.' : 'You still have access until the end of the period.'}
              </p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setShowCancelModal(false)} variant="outline" className="flex-1 h-12 rounded-xl font-bold">
                {language === 'nl' ? 'Behouden' : 'Keep'}
              </Button>
              <Button onClick={handleCancelSubscription} disabled={cancelling} className="flex-1 h-12 bg-red-500 hover:bg-red-600 text-white rounded-xl font-bold">
                {cancelling ? <Loader2 className="w-5 h-5 animate-spin" /> : (language === 'nl' ? 'Opzeggen' : 'Cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0D2137]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-lg font-bold text-white">{language === 'nl' ? 'Mijn Profiel' : 'My Profile'}</h1>
          <div className="w-10 h-10" /> {/* Spacer for symmetry */}
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 pb-32 space-y-6">
        
        {/* Profile Hero Card */}
        <div className="relative overflow-hidden rounded-3xl bg-white shadow-2xl">
          {/* Avatar Section */}
          <div className={`relative ${currentAvatar.bg} p-8 text-center`}>
            {/* Premium Badge */}
            {hasSubscription && (
              <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5">
                <Crown className="w-4 h-4 text-yellow-300" />
                <span className="text-white text-xs font-bold">Premium</span>
              </div>
            )}
            
            {/* Profile Photo or Avatar */}
            <div className="relative inline-block">
              {profileImage ? (
                <div className="relative group">
                  <img 
                    src={profileImage} 
                    alt="Profile" 
                    className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-xl group-hover:scale-105 transition-transform"
                  />
                  <label className="absolute -bottom-1 -right-1 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-[#F7F7F7] transition-colors">
                    {uploadingImage ? <Loader2 className="w-5 h-5 text-[#2E7D32] animate-spin" /> : <Camera className="w-5 h-5 text-[#2E7D32]" />}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
                  </label>
                </div>
              ) : (
                <div className="relative group">
                  <button
                    onClick={() => setShowAvatarModal(true)}
                    className="w-28 h-28 bg-white rounded-full flex items-center justify-center text-6xl shadow-xl group-hover:scale-105 transition-transform"
                  >
                    {currentAvatar.emoji}
                  </button>
                  <label className="absolute -bottom-1 -right-1 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-[#F7F7F7] transition-colors">
                    {uploadingImage ? <Loader2 className="w-5 h-5 text-[#2E7D32] animate-spin" /> : <Camera className="w-5 h-5 text-[#9E9E9E]" />}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
                  </label>
                </div>
              )}
            </div>
            
            <h2 className="text-2xl font-black text-white mt-5 drop-shadow-lg">{user?.name}</h2>
            {currentProfileType && (
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mt-3">
                <span className="text-xl">{currentProfileType.emoji}</span>
                <span className="text-white font-semibold">{currentProfileType.label}</span>
              </div>
            )}
          </div>
          
          {/* Profile Completeness */}
          <div className="px-6 py-4 bg-white border-b border-[#E5E5E5]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-[#2A2A2A]">{language === 'nl' ? 'Profiel volledigheid' : 'Profile completeness'}</span>
              <span className={`text-sm font-black ${completeness >= 80 ? 'text-[#2E7D32]' : completeness >= 50 ? 'text-[#FF9800]' : 'text-red-500'}`}>{completeness}%</span>
            </div>
            <div className="h-2 bg-[#E5E5E5] rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${completeness >= 80 ? 'bg-[#2E7D32]' : completeness >= 50 ? 'bg-[#FF9800]' : 'bg-red-500'}`}
                style={{ width: `${completeness}%` }}
              ></div>
            </div>
            {completeness < 100 && (
              <p className="text-xs text-[#9E9E9E] mt-2">
                {language === 'nl' ? '💡 Tip: Een volledig profiel krijgt 3x meer matches!' : '💡 Tip: A complete profile gets 3x more matches!'}
              </p>
            )}
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-3 divide-x divide-[#E5E5E5]">
            <div className="p-5 text-center group hover:bg-[#2E7D32]/5 transition-colors">
              <div className="w-12 h-12 bg-[#2E7D32]/10 rounded-2xl flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                <MapPin className="w-6 h-6 text-[#2E7D32]" />
              </div>
              <div className="text-2xl font-black text-[#2E7D32]">{preferences.cities?.length || 0}</div>
              <div className="text-xs text-[#9E9E9E] font-medium">{language === 'nl' ? 'Steden' : 'Cities'}</div>
            </div>
            <div className="p-5 text-center group hover:bg-[#1F3A5F]/5 transition-colors">
              <div className="w-12 h-12 bg-[#1F3A5F]/10 rounded-2xl flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                <Building2 className="w-6 h-6 text-[#1F3A5F]" />
              </div>
              <div className="text-2xl font-black text-[#1F3A5F]">{preferences.types?.length || 0}</div>
              <div className="text-xs text-[#9E9E9E] font-medium">{language === 'nl' ? 'Types' : 'Types'}</div>
            </div>
            <div className="p-5 text-center group hover:bg-[#FF9800]/5 transition-colors">
              <div className="w-12 h-12 bg-[#FF9800]/10 rounded-2xl flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                <Euro className="w-6 h-6 text-[#FF9800]" />
              </div>
              <div className="text-2xl font-black text-[#FF9800]">
                {preferences.maxPrice ? `€${preferences.maxPrice}` : '∞'}
              </div>
              <div className="text-xs text-[#9E9E9E] font-medium">{language === 'nl' ? 'Max budget' : 'Max budget'}</div>
            </div>
          </div>
        </div>

        {/* About Me Section */}
        {!isVerhuurder && (
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="p-5 bg-[#2E7D32] flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-black text-white">{language === 'nl' ? 'Over mij' : 'About me'}</h3>
                <p className="text-white/70 text-sm">{language === 'nl' ? 'Maak een goede eerste indruk' : 'Make a great first impression'}</p>
              </div>
            </div>
            
            <div className="p-5">
              <Textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder={language === 'nl' 
                  ? 'Vertel verhuurders wie je bent, wat je doet en waarom je de perfecte huurder bent...' 
                  : 'Tell landlords who you are, what you do and why you are the perfect tenant...'}
                className="min-h-[140px] rounded-2xl border-2 border-[#E5E5E5] focus:border-[#2E7D32] resize-none text-base"
                maxLength={500}
              />
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${bio.length > 400 ? 'text-[#FF9800]' : 'text-[#9E9E9E]'}`}>{bio.length}/500</span>
                  {bio.length < 50 && <span className="text-xs text-[#9E9E9E]">💡 Min. 50 tekens voor beste resultaat</span>}
                </div>
                <Button
                  onClick={handleSaveBio}
                  className="bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-full px-6 h-11 font-bold"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {language === 'nl' ? 'Opslaan' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Search Preferences - Preview or Edit */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="p-5 bg-[#FF9800] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-black text-white">{language === 'nl' ? 'Zoekvoorkeuren' : 'Search preferences'}</h3>
                <p className="text-white/70 text-sm">{language === 'nl' ? 'Wat zoek je precies?' : 'What are you looking for?'}</p>
              </div>
            </div>
            <button 
              onClick={() => setEditMode(!editMode)}
              className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              {editMode ? <Check className="w-5 h-5 text-white" /> : <Edit2 className="w-5 h-5 text-white" />}
            </button>
          </div>
          
          {!editMode ? (
            /* Preview Mode */
            <div className="p-5 space-y-4">
              {preferences.cities?.length > 0 && (
                <div className="flex items-start gap-4 p-4 bg-[#2E7D32]/5 rounded-2xl">
                  <div className="w-12 h-12 bg-[#2E7D32] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#2E7D32]/30">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-[#2E7D32] font-bold uppercase tracking-wide mb-1">{language === 'nl' ? 'Locatie' : 'Location'}</p>
                    <p className="font-bold text-[#2A2A2A] text-lg">{preferences.cities.join(', ')}</p>
                    {preferences.searchRadius > 0 && (
                      <p className="text-sm text-[#2E7D32] mt-1">
                        + {preferences.searchRadius} km {language === 'nl' ? 'radius' : 'radius'}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {preferences.types?.length > 0 && (
                <div className="flex items-start gap-4 p-4 bg-[#1F3A5F]/5 rounded-2xl">
                  <div className="w-12 h-12 bg-[#1F3A5F] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#1F3A5F]/30">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-[#1F3A5F] font-bold uppercase tracking-wide mb-1">{language === 'nl' ? 'Woningtype' : 'Property type'}</p>
                    <p className="font-bold text-[#2A2A2A] text-lg capitalize">{preferences.types.join(', ')}</p>
                  </div>
                </div>
              )}

              {(preferences.minPrice || preferences.maxPrice) && (
                <div className="flex items-start gap-4 p-4 bg-[#FF9800]/5 rounded-2xl">
                  <div className="w-12 h-12 bg-[#FF9800] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#FF9800]/30">
                    <Euro className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-[#FF9800] font-bold uppercase tracking-wide mb-1">{language === 'nl' ? 'Budget' : 'Budget'}</p>
                    <p className="font-black text-[#2A2A2A] text-xl">
                      €{preferences.minPrice || 0} - €{preferences.maxPrice || '∞'} <span className="text-sm font-medium text-[#9E9E9E]">/{language === 'nl' ? 'mnd' : 'mo'}</span>
                    </p>
                  </div>
                </div>
              )}

              {preferences.rentalDuration && (
                <div className="flex items-start gap-4 p-4 bg-[#9C27B0]/5 rounded-2xl">
                  <div className="w-12 h-12 bg-[#9C27B0] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#9C27B0]/30">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-[#9C27B0] font-bold uppercase tracking-wide mb-1">{language === 'nl' ? 'Huurtermijn' : 'Rental period'}</p>
                    <p className="font-bold text-[#2A2A2A] text-lg">
                      {RENTAL_DURATION_OPTIONS.find(d => d.id === preferences.rentalDuration)?.label[language] || preferences.rentalDuration}
                    </p>
                  </div>
                </div>
              )}

              {/* Huisdieren */}
              {preferences.hasPets !== null && (
                <div className="flex items-start gap-4 p-4 bg-[#795548]/5 rounded-2xl">
                  <div className="w-12 h-12 bg-[#795548] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#795548]/30">
                    <span className="text-2xl">🐾</span>
                  </div>
                  <div>
                    <p className="text-xs text-[#795548] font-bold uppercase tracking-wide mb-1">{language === 'nl' ? 'Huisdieren' : 'Pets'}</p>
                    <p className="font-bold text-[#2A2A2A] text-lg">
                      {preferences.hasPets 
                        ? (language === 'nl' ? 'Ja, ik heb huisdier(en)' : 'Yes, I have pet(s)') 
                        : (language === 'nl' ? 'Nee, geen huisdieren' : 'No pets')}
                    </p>
                  </div>
                </div>
              )}

              {/* Inschrijving */}
              {preferences.needsRegistration !== null && (
                <div className="flex items-start gap-4 p-4 bg-[#607D8B]/5 rounded-2xl">
                  <div className="w-12 h-12 bg-[#607D8B] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#607D8B]/30">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-[#607D8B] font-bold uppercase tracking-wide mb-1">{language === 'nl' ? 'Inschrijving' : 'Registration'}</p>
                    <p className="font-bold text-[#2A2A2A] text-lg">
                      {preferences.needsRegistration 
                        ? (language === 'nl' ? 'Ja, inschrijving nodig' : 'Yes, registration needed') 
                        : (language === 'nl' ? 'Nee, niet nodig' : 'No, not needed')}
                    </p>
                  </div>
                </div>
              )}

              {/* Automatisch Bericht */}
              {preferences.autoMessage && (
                <div className="flex items-start gap-4 p-4 bg-[#2E7D32]/5 rounded-2xl">
                  <div className="w-12 h-12 bg-[#2E7D32] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#2E7D32]/30">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-[#2E7D32] font-bold uppercase tracking-wide mb-1">{language === 'nl' ? 'Automatisch bericht' : 'Auto message'}</p>
                    <p className="font-medium text-[#2A2A2A] text-sm leading-relaxed">{preferences.autoMessage}</p>
                    <p className="text-xs text-[#9E9E9E] mt-1">{language === 'nl' ? 'Dit bericht wordt automatisch verstuurd als je een woning liked' : 'This message is sent automatically when you like a property'}</p>
                  </div>
                </div>
              )}

              {!preferences.cities?.length && !preferences.types?.length && !preferences.maxPrice && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-[#F7F7F7] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-[#9E9E9E]" />
                  </div>
                  <p className="text-[#9E9E9E] font-medium">{language === 'nl' ? 'Nog geen voorkeuren ingesteld' : 'No preferences set yet'}</p>
                  <Button onClick={() => setEditMode(true)} className="mt-4 bg-[#2E7D32] text-white rounded-full">
                    <Edit2 className="w-4 h-4 mr-2" />
                    {language === 'nl' ? 'Voorkeuren instellen' : 'Set preferences'}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            /* Edit Mode */
            <div className="p-5 space-y-6">
              {/* Profile Type */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block">{language === 'nl' ? 'Ik ben een...' : 'I am a...'}</label>
                <div className="grid grid-cols-3 gap-2">
                  {profileTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setPreferences(prev => ({ ...prev, profileType: type.id }))}
                      className={`p-3 rounded-2xl border-2 text-center transition-all relative ${
                        preferences.profileType === type.id
                          ? 'border-[#2E7D32] bg-[#2E7D32] scale-105'
                          : 'border-[#E5E5E5] hover:border-[#2E7D32]/50'
                      }`}
                    >
                      {preferences.profileType === type.id && (
                        <Check className="w-4 h-4 text-white absolute top-1 right-1" />
                      )}
                      <span className="text-2xl block mb-1">{type.emoji}</span>
                      <span className={`text-xs font-bold ${preferences.profileType === type.id ? 'text-white' : 'text-[#2A2A2A]'}`}>{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Cities */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block">{language === 'nl' ? 'Waar zoek je?' : 'Where are you looking?'}</label>
                
                {/* Search input */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9E9E9E]" />
                  <Input
                    type="text"
                    placeholder={language === 'nl' ? 'Zoek stad...' : 'Search city...'}
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    className="pl-10 h-12 rounded-xl border-2 border-[#E5E5E5] focus:border-[#2E7D32]"
                  />
                </div>

                {/* Selected cities */}
                {preferences.cities?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {preferences.cities.map((city) => (
                      <button
                        key={city}
                        onClick={() => setPreferences(prev => ({ ...prev, cities: prev.cities.filter(c => c !== city) }))}
                        className="px-3 py-1.5 bg-[#2E7D32] text-white rounded-full text-sm font-bold flex items-center gap-1"
                      >
                        {city}
                        <X className="w-4 h-4" />
                      </button>
                    ))}
                  </div>
                )}

                {/* Popular cities */}
                {popularCities.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-[#9E9E9E] font-bold uppercase mb-2">{language === 'nl' ? 'Populair' : 'Popular'}</p>
                    <div className="flex flex-wrap gap-2">
                      {popularCities.map((city) => (
                        <button
                          key={city.id}
                          onClick={() => {
                            const cities = preferences.cities || [];
                            if (!cities.includes(city.id)) {
                              setPreferences(prev => ({ ...prev, cities: [...cities, city.id] }));
                            }
                          }}
                          disabled={preferences.cities?.includes(city.id)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            preferences.cities?.includes(city.id)
                              ? 'bg-[#E5E5E5] text-[#9E9E9E]'
                              : 'bg-[#F7F7F7] text-[#2A2A2A] hover:bg-[#2E7D32] hover:text-white'
                          }`}
                        >
                          {city.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Other cities */}
                {otherCities.length > 0 && (
                  <div>
                    <p className="text-xs text-[#9E9E9E] font-bold uppercase mb-2">{language === 'nl' ? 'Andere steden' : 'Other cities'}</p>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                      {otherCities.map((city) => (
                        <button
                          key={city.id}
                          onClick={() => {
                            const cities = preferences.cities || [];
                            if (!cities.includes(city.id)) {
                              setPreferences(prev => ({ ...prev, cities: [...cities, city.id] }));
                            }
                          }}
                          disabled={preferences.cities?.includes(city.id)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            preferences.cities?.includes(city.id)
                              ? 'bg-[#E5E5E5] text-[#9E9E9E]'
                              : 'bg-[#F7F7F7] text-[#2A2A2A] hover:bg-[#2E7D32] hover:text-white'
                          }`}
                        >
                          {city.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Search Radius */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block">{language === 'nl' ? 'Zoekradius' : 'Search radius'}</label>
                <p className="text-xs text-[#9E9E9E] mb-3">{language === 'nl' ? 'Toon ook woningen binnen deze afstand van je gekozen steden' : 'Also show properties within this distance from your chosen cities'}</p>
                <div className="grid grid-cols-3 gap-2">
                  {RADIUS_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setPreferences(prev => ({ ...prev, searchRadius: option.value }))}
                      className={`p-3 rounded-xl border-2 text-center transition-all relative ${
                        preferences.searchRadius === option.value
                          ? 'border-[#2E7D32] bg-[#2E7D32] text-white'
                          : 'border-[#E5E5E5] text-[#2A2A2A] hover:border-[#2E7D32]/50'
                      }`}
                    >
                      {preferences.searchRadius === option.value && (
                        <Check className="w-4 h-4 absolute top-1 right-1" />
                      )}
                      <span className="text-sm font-bold">{option.value === 0 ? (language === 'nl' ? 'Exact' : 'Exact') : option.label[language]}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Property Types */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block">{language === 'nl' ? 'Type woning' : 'Property type'}</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'kamer', label: 'Kamer' },
                    { id: 'studio', label: 'Studio' },
                    { id: 'appartement', label: 'Appartement' },
                    { id: 'eengezinswoning', label: 'Woning' }
                  ].map((type) => (
                    <button
                      key={type.id}
                      onClick={() => {
                        const types = preferences.types || [];
                        if (types.includes(type.id)) {
                          setPreferences(prev => ({ ...prev, types: types.filter(t => t !== type.id) }));
                        } else {
                          setPreferences(prev => ({ ...prev, types: [...types, type.id] }));
                        }
                      }}
                      className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
                        preferences.types?.includes(type.id)
                          ? 'bg-[#1F3A5F] text-white shadow-lg shadow-[#1F3A5F]/30'
                          : 'bg-[#F7F7F7] text-[#2A2A2A] hover:bg-[#E5E5E5]'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block">{language === 'nl' ? 'Budget per maand' : 'Monthly budget'}</label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E9E9E] font-bold">€</span>
                    <Input
                      type="number"
                      placeholder="Min"
                      className="h-12 pl-8 rounded-xl border-2 border-[#E5E5E5] focus:border-[#2E7D32]"
                      value={preferences.minPrice}
                      onChange={(e) => setPreferences(prev => ({ ...prev, minPrice: e.target.value }))}
                    />
                  </div>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E9E9E] font-bold">€</span>
                    <Input
                      type="number"
                      placeholder="Max"
                      className="h-12 pl-8 rounded-xl border-2 border-[#E5E5E5] focus:border-[#2E7D32]"
                      value={preferences.maxPrice}
                      onChange={(e) => setPreferences(prev => ({ ...prev, maxPrice: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Rental Duration */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block">{language === 'nl' ? 'Gewenste huurtermijn' : 'Desired rental period'}</label>
                <div className="grid grid-cols-2 gap-2">
                  {RENTAL_DURATION_OPTIONS.map((duration) => (
                    <button
                      key={duration.id}
                      onClick={() => setPreferences(prev => ({ ...prev, rentalDuration: duration.id }))}
                      className={`p-3 rounded-xl border-2 text-sm font-bold transition-all relative ${
                        preferences.rentalDuration === duration.id
                          ? 'border-[#2E7D32] bg-[#2E7D32] text-white'
                          : 'border-[#E5E5E5] text-[#2A2A2A] hover:border-[#2E7D32]/50'
                      }`}
                    >
                      {preferences.rentalDuration === duration.id && (
                        <Check className="w-4 h-4 absolute top-2 right-2" />
                      )}
                      {duration.label[language]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Huisdieren */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block">{language === 'nl' ? 'Heb je huisdieren?' : 'Do you have pets?'}</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, hasPets: true }))}
                    className={`p-4 rounded-xl border-2 text-center transition-all relative ${
                      preferences.hasPets === true
                        ? 'border-[#2E7D32] bg-[#2E7D32] text-white'
                        : 'border-[#E5E5E5] hover:border-[#2E7D32]/50'
                    }`}
                  >
                    {preferences.hasPets === true && (
                      <Check className="w-5 h-5 absolute top-2 right-2 text-white" />
                    )}
                    <span className="text-2xl block mb-1">🐾</span>
                    <span className={`text-sm font-bold ${preferences.hasPets === true ? 'text-white' : 'text-[#2A2A2A]'}`}>
                      {language === 'nl' ? 'Ja' : 'Yes'}
                    </span>
                  </button>
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, hasPets: false }))}
                    className={`p-4 rounded-xl border-2 text-center transition-all relative ${
                      preferences.hasPets === false
                        ? 'border-[#2E7D32] bg-[#2E7D32] text-white'
                        : 'border-[#E5E5E5] hover:border-[#2E7D32]/50'
                    }`}
                  >
                    {preferences.hasPets === false && (
                      <Check className="w-5 h-5 absolute top-2 right-2 text-white" />
                    )}
                    <span className="text-2xl block mb-1">🚫</span>
                    <span className={`text-sm font-bold ${preferences.hasPets === false ? 'text-white' : 'text-[#2A2A2A]'}`}>
                      {language === 'nl' ? 'Nee' : 'No'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Inschrijving nodig */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block">{language === 'nl' ? 'Inschrijving op adres nodig?' : 'Need address registration?'}</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, needsRegistration: true }))}
                    className={`p-4 rounded-xl border-2 text-center transition-all relative ${
                      preferences.needsRegistration === true
                        ? 'border-[#2E7D32] bg-[#2E7D32]'
                        : 'border-[#E5E5E5] hover:border-[#2E7D32]/50'
                    }`}
                  >
                    {preferences.needsRegistration === true && (
                      <Check className="w-5 h-5 absolute top-2 right-2 text-white" />
                    )}
                    <Shield className={`w-6 h-6 mx-auto mb-1 ${preferences.needsRegistration === true ? 'text-white' : 'text-[#607D8B]'}`} />
                    <span className={`text-sm font-bold ${preferences.needsRegistration === true ? 'text-white' : 'text-[#2A2A2A]'}`}>
                      {language === 'nl' ? 'Ja, nodig' : 'Yes, needed'}
                    </span>
                  </button>
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, needsRegistration: false }))}
                    className={`p-4 rounded-xl border-2 text-center transition-all relative ${
                      preferences.needsRegistration === false
                        ? 'border-[#2E7D32] bg-[#2E7D32]'
                        : 'border-[#E5E5E5] hover:border-[#2E7D32]/50'
                    }`}
                  >
                    {preferences.needsRegistration === false && (
                      <Check className="w-5 h-5 absolute top-2 right-2 text-white" />
                    )}
                    <X className={`w-6 h-6 mx-auto mb-1 ${preferences.needsRegistration === false ? 'text-white' : 'text-[#9E9E9E]'}`} />
                    <span className={`text-sm font-bold ${preferences.needsRegistration === false ? 'text-white' : 'text-[#2A2A2A]'}`}>
                      {language === 'nl' ? 'Niet nodig' : 'Not needed'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Automatisch Bericht */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-1 block">{language === 'nl' ? 'Automatisch bericht bij like' : 'Auto message on like'}</label>
                <p className="text-xs text-[#9E9E9E] mb-3">
                  {language === 'nl' 
                    ? 'Dit bericht wordt automatisch naar de verhuurder gestuurd als je een woning liked (rechts swipet).' 
                    : 'This message is automatically sent to the landlord when you like (right swipe) a property.'}
                </p>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-[#9E9E9E]" />
                  <Textarea
                    data-testid="auto-message-input"
                    placeholder={language === 'nl' 
                      ? 'Bijv: Hallo! Ik ben een student op zoek naar een kamer. Graag zou ik meer willen weten over deze woning.' 
                      : 'E.g.: Hi! I am a student looking for a room. I would like to know more about this property.'}
                    value={preferences.autoMessage}
                    onChange={(e) => setPreferences(prev => ({ ...prev, autoMessage: e.target.value }))}
                    className="pl-10 min-h-[100px] rounded-xl border-2 border-[#E5E5E5] focus:border-[#2E7D32] resize-none"
                    maxLength={500}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-[#9E9E9E]">
                      {language === 'nl' ? 'Laat leeg voor een standaard bericht' : 'Leave empty for a default message'}
                    </span>
                    <span className={`text-xs font-bold ${(preferences.autoMessage?.length || 0) > 450 ? 'text-[#C04A4A]' : 'text-[#9E9E9E]'}`}>
                      {preferences.autoMessage?.length || 0}/500
                    </span>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSavePreferences}
                disabled={saving}
                className="w-full h-14 bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-2xl font-bold text-lg"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
                {language === 'nl' ? 'Voorkeuren opslaan' : 'Save preferences'}
              </Button>
            </div>
          )}
        </div>

        {/* 100% Gratis Status */}
        <div className="bg-[#2E7D32]/5 rounded-3xl p-6 border-2 border-[#2E7D32]/20 shadow-xl">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-[#2E7D32] rounded-2xl flex items-center justify-center shadow-lg">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-black text-[#2A2A2A]">{language === 'nl' ? '100% Gratis' : '100% Free'}</h3>
              <p className="text-[#2E7D32] font-medium">{language === 'nl' ? 'Alle functies beschikbaar' : 'All features available'}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-[#2E7D32]/20">
            <div className="flex items-center gap-2 text-[#2A2A2A]">
              <Check className="w-4 h-4 text-[#2E7D32]" />
              <span className="text-sm font-medium">{language === 'nl' ? 'Onbeperkt swipen' : 'Unlimited swipes'}</span>
            </div>
            <div className="flex items-center gap-2 text-[#2A2A2A]">
              <Check className="w-4 h-4 text-[#2E7D32]" />
              <span className="text-sm font-medium">{language === 'nl' ? 'Direct contact' : 'Direct contact'}</span>
            </div>
            <div className="flex items-center gap-2 text-[#2A2A2A]">
              <Check className="w-4 h-4 text-[#2E7D32]" />
              <span className="text-sm font-medium">{language === 'nl' ? 'Auto berichten' : 'Auto messages'}</span>
            </div>
            <div className="flex items-center gap-2 text-[#2A2A2A]">
              <Check className="w-4 h-4 text-[#2E7D32]" />
              <span className="text-sm font-medium">{language === 'nl' ? 'Geen verborgen kosten' : 'No hidden costs'}</span>
            </div>
          </div>
        </div>

        {/* Settings */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="p-5 bg-[#F7F7F7] border-b border-[#E5E5E5]">
            <h3 className="text-lg font-black text-[#2A2A2A]">{language === 'nl' ? 'Instellingen' : 'Settings'}</h3>
          </div>
          
          <div className="divide-y divide-[#E5E5E5]">
            {/* Notifications */}
            <button onClick={toggleNotifications} className="w-full p-5 flex items-center justify-between hover:bg-[#F7F7F7] transition-colors">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${emailNotifications ? 'bg-[#2E7D32]/10' : 'bg-[#9E9E9E]/10'}`}>
                  {emailNotifications ? <Bell className="w-6 h-6 text-[#2E7D32]" /> : <BellOff className="w-6 h-6 text-[#9E9E9E]" />}
                </div>
                <div className="text-left">
                  <p className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'E-mail notificaties' : 'Email notifications'}</p>
                  <p className="text-sm text-[#9E9E9E]">{emailNotifications ? (language === 'nl' ? 'Aan' : 'On') : (language === 'nl' ? 'Uit' : 'Off')}</p>
                </div>
              </div>
              <div className={`w-14 h-8 rounded-full p-1 transition-colors ${emailNotifications ? 'bg-[#2E7D32]' : 'bg-[#E5E5E5]'}`}>
                <div className={`w-6 h-6 bg-white rounded-full shadow transition-transform ${emailNotifications ? 'translate-x-6' : ''}`}></div>
              </div>
            </button>

            {/* Logout */}
            <button onClick={handleLogout} className="w-full p-5 flex items-center gap-4 hover:bg-red-50 transition-colors group">
              <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center group-hover:bg-red-200 transition-colors">
                <LogOut className="w-6 h-6 text-red-500" />
              </div>
              <div className="text-left">
                <p className="font-bold text-red-500">{language === 'nl' ? 'Uitloggen' : 'Log out'}</p>
                <p className="text-sm text-[#9E9E9E]">{user?.email}</p>
              </div>
            </button>
          </div>
        </div>

      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-[#E5E5E5] safe-area-pb">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-around">
          <button onClick={() => navigate('/swipematch/swipe')} className="flex flex-col items-center gap-1 p-2">
            <Heart className="w-6 h-6 text-[#9E9E9E]" />
            <span className="text-xs text-[#9E9E9E]">Swipen</span>
          </button>
          <button onClick={() => navigate('/dashboard')} className="flex flex-col items-center gap-1 p-2">
            <Home className="w-6 h-6 text-[#9E9E9E]" />
            <span className="text-xs text-[#9E9E9E]">Dashboard</span>
          </button>
          <button className="flex flex-col items-center gap-1 p-2">
            <div className="w-6 h-6 bg-[#2E7D32] rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            <span className="text-xs font-bold text-[#2E7D32]">Profiel</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
